import { useEffect, useState } from 'react';
import { Loader2, CheckCircle2, XCircle, ChevronRight, Bot, Wrench, ShieldCheck, FileText } from 'lucide-react';
import { RUN_IN_FLIGHT, type RunState } from '@warriorlab/shared';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Markdown } from '@/components/ui/Markdown';
import { Spinner } from '@/components/ui/States';
import { toast } from '@/components/ui/Toast';
import { humanTitle } from '@/lib/humanTitle';
import { api, type RunDetail, type RunEventItem } from '@/lib/api';

const inFlightState = (s: RunState) => RUN_IN_FLIGHT.includes(s);
export const runStateTone = (s: RunState) =>
  s === 'DELIVERED' ? 'success' : s === 'FAILED' ? 'danger' : 'warning';

/** Client-friendly verbs for raw tool names — "Ran a command", not "→ Bash". */
const TOOL_VERB: Record<string, string> = {
  Bash: 'Ran a command',
  Read: 'Read a file',
  Write: 'Wrote a file',
  Edit: 'Edited a file',
  Grep: 'Searched the code',
  Glob: 'Listed files',
  WebFetch: 'Fetched a web page',
  WebSearch: 'Searched the web',
  TodoWrite: 'Updated its task list',
  Task: 'Delegated to a specialist',
  NotebookEdit: 'Edited a notebook',
};
const toolVerb = (name: string) => TOOL_VERB[name] ?? `Used ${name}`;

/** Connector names → client vocabulary. */
const SOURCE_LABEL: Record<string, string> = {
  fireflies: 'Fireflies',
  gdrive: 'Google Drive',
  google_drive: 'Google Drive',
};
const sourceLabel = (name: string) => {
  const key = name.toLowerCase();
  return SOURCE_LABEL[key] ?? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

type FeedItem =
  | { kind: 'line'; icon: 'bot' | 'tool' | 'step' | 'info'; text: string; count?: number }
  | { kind: 'chips'; label: string; chips: string[] }
  | { kind: 'shield'; text: string }
  | { kind: 'deliverable'; text: string; isError: boolean };

/** Translate one streamed event into a feed item; null = hide (noise / unknown).
 * Payload shapes may drift as the worker evolves — stay tolerant, never throw. */
function itemFor(ev: RunEventItem): FeedItem | null {
  const p = (ev.payload ?? {}) as Record<string, any>;
  switch (ev.type) {
    case 'knowledge':
      return { kind: 'line', icon: 'info', text: `Read ${p.loaded} knowledge item(s) from this workspace` };
    case 'agents':
      return { kind: 'chips', label: 'Agents', chips: (p.written ?? []).map((s: string) => humanTitle(s)) };
    case 'credential':
      return { kind: 'line', icon: 'info', text: `Running on the ${p.source} Claude subscription` };
    case 'connectors': {
      // attached may be { name, host }[] or plain strings.
      const names: string[] = (p.attached ?? []).map((a: any) => sourceLabel(typeof a === 'string' ? a : a?.name ?? ''));
      const chips = names.filter(Boolean);
      return chips.length ? { kind: 'chips', label: 'Sources', chips } : null;
    }
    case 'egress_blocked':
      return {
        kind: 'shield',
        text: `Blocked an outbound call${p.host ? ` to ${p.host}` : ''}. This run can only reach its approved sources.`,
      };
    case 'system': {
      if (p.subtype === 'task_progress' && p.description) return { kind: 'line', icon: 'step', text: p.description };
      if (p.subtype === 'task_notification' && p.summary) return { kind: 'line', icon: 'step', text: `✓ ${p.summary}` };
      if (p.model && p.cwd) return { kind: 'line', icon: 'info', text: `Agent started (model ${p.model})` };
      return null;
    }
    case 'assistant': {
      if (p.error === 'rate_limit') return { kind: 'line', icon: 'tool', text: 'Rate limit hit (429)' };
      const content = p.message?.content;
      if (Array.isArray(content)) {
        const text = content.filter((c: any) => c.type === 'text').map((c: any) => c.text).join('').trim();
        if (text) return { kind: 'line', icon: 'bot', text };
        const tool = content.find((c: any) => c.type === 'tool_use');
        if (tool) return { kind: 'line', icon: 'tool', text: toolVerb(tool.name) };
      }
      return null;
    }
    case 'style_warnings': {
      // Warn-only house-style check from ingest. Staff-only in practice: the
      // API's client redaction (CLIENT_SAFE_EVENT_TYPES) drops this event
      // type for client users, deliberately.
      const files = (p.files ?? []) as { path?: string; count?: number }[];
      const names = files.map((f) => f.path).filter(Boolean);
      if (!names.length) return null;
      const total = files.reduce((n, f) => n + (f.count ?? 0), 0);
      return {
        kind: 'line',
        icon: 'info',
        text: `Style check: ${total} pattern${total === 1 ? '' : 's'} to review in ${names.join(', ')}`,
      };
    }
    case 'result':
      return p.result ? { kind: 'deliverable', text: String(p.result), isError: Boolean(p.is_error) } : null;
    default:
      return null; // user tool_results, raw state, unknown event types — hidden
  }
}

/** Collapse runs of identical tool lines ("Read a file ×6") so long transcripts stay scannable. */
function buildFeed(events: RunEventItem[]): FeedItem[] {
  const out: FeedItem[] = [];
  for (const ev of events) {
    const item = itemFor(ev);
    if (!item) continue;
    const prev = out[out.length - 1];
    if (item.kind === 'line' && item.icon === 'tool' && prev?.kind === 'line' && prev.icon === 'tool' && prev.text === item.text) {
      prev.count = (prev.count ?? 1) + 1;
      continue;
    }
    out.push(item);
  }
  return out;
}

const ICON = {
  bot: <Bot className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />,
  tool: <Wrench className="mt-0.5 h-3.5 w-3.5 shrink-0 text-fg-subtle" />,
  step: <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-fg-subtle" />,
  info: <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-border" />,
};

/** The readable event feed for one run. */
export function TranscriptFeed({ events, inFlight }: { events: RunEventItem[]; inFlight?: boolean }) {
  const feed = buildFeed(events);
  if (feed.length === 0) {
    return <p className="text-sm text-fg-muted">{inFlight ? 'Waiting for the first output…' : 'No transcript.'}</p>;
  }
  return (
    <ul className="space-y-2">
      {feed.map((item, i) => {
        if (item.kind === 'chips') {
          return (
            <li key={i} className="flex flex-wrap items-center gap-1.5 text-sm">
              <span className="text-fg-subtle">{item.label}:</span>
              {item.chips.map((c, j) => (
                <Badge key={j} tone="neutral">{c}</Badge>
              ))}
            </li>
          );
        }
        if (item.kind === 'shield') {
          return (
            <li key={i} className="flex gap-2 text-sm">
              <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
              <span className="min-w-0 break-words text-fg-muted">{item.text}</span>
            </li>
          );
        }
        if (item.kind === 'deliverable') {
          return (
            <li
              key={i}
              className={`rounded-lg border p-3 ${item.isError ? 'border-danger/40' : 'border-success/40'}`}
            >
              <p className={`mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${item.isError ? 'text-danger' : 'text-success'}`}>
                <FileText className="h-3.5 w-3.5" aria-hidden />
                {item.isError ? 'Error' : 'Deliverable'}
              </p>
              <div className="max-h-96 overflow-y-auto">
                <Markdown className="text-sm">{item.text}</Markdown>
              </div>
            </li>
          );
        }
        return (
          <li key={i} className="flex gap-2 text-sm">
            {ICON[item.icon]}
            <span className="min-w-0 whitespace-pre-wrap break-words text-fg-muted">
              {item.text}
              {item.count && item.count > 1 && <span className="ml-1.5 text-xs text-fg-subtle">×{item.count}</span>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

const STATE_COPY: Record<RunState, string> = {
  REQUESTED: 'Queued…',
  PREPARING: 'Preparing the run…',
  RUNNING: 'Running the agent…',
  INGESTING: 'Ingesting results…',
  DELIVERED: 'Completed',
  FAILED: 'Failed',
};

/** Live transcript for one run: status header + reason + the readable event
 * feed, polling every 3s while the run is in flight. Ids come via props — no
 * ambient tenant state. `onRetryStarted` lets the page navigate to the new run. */
export function RunTranscript({
  runId,
  onRetryStarted,
}: {
  runId: string;
  onRetryStarted?: (newRunId: string) => void;
}) {
  const [run, setRun] = useState<RunDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;
    let errCount = 0;
    setRun(null);
    setErr(null);
    const tick = async () => {
      try {
        const d = await api.getRun(runId);
        if (!alive) return;
        errCount = 0;
        setRun(d);
        setErr(null);
        if (inFlightState(d.state)) timer = setTimeout(tick, 3000);
      } catch (e) {
        if (!alive) return;
        errCount += 1;
        setErr(e instanceof Error ? e.message : 'Failed to load run');
        // A transient blip must not freeze the transcript: keep polling with a
        // longer interval, and after several consecutive failures fall back to
        // the manual Retry affordance rather than hammering a dead endpoint.
        if (errCount < 5) timer = setTimeout(tick, 5000);
      }
    };
    tick();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [runId, reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  const [canceling, setCanceling] = useState(false);
  const inFlight = run ? inFlightState(run.state) : false;
  const canRetry = run?.state === 'FAILED' && !!onRetryStarted;

  async function cancel() {
    if (!run || canceling) return;
    setCanceling(true);
    try {
      await api.cancelRun(run.id);
      toast.success('Canceling the run');
      reload();
    } catch (e) {
      toast.danger('Could not cancel', e instanceof Error ? e.message : undefined);
    } finally {
      setCanceling(false);
    }
  }

  async function retry() {
    if (!run || retrying) return;
    setRetrying(true);
    try {
      const newRun = await api.startRun(run.projectId, {
        agentId: run.agentId,
        ...(run.input ? { input: run.input } : {}),
      });
      toast.success('Run started');
      onRetryStarted?.(newRun.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to start run');
    } finally {
      setRetrying(false);
    }
  }

  if (err && !run)
    return (
      <div className="flex flex-col items-start gap-2 rounded-lg border border-danger/40 p-4 text-sm">
        <p className="text-danger">{err}</p>
        <Button variant="secondary" size="sm" onClick={reload}>Retry</Button>
      </div>
    );
  if (!run) return <Spinner label="Loading run…" />;

  return (
    <div className="space-y-4">
      {err && (
        <div className="flex items-center gap-2 text-sm text-danger">
          <span>{err}</span>
          <Button variant="secondary" size="sm" onClick={reload}>Retry</Button>
        </div>
      )}

      {/* Status banner */}
      <div className="flex items-start gap-2 rounded-lg border border-border bg-bg p-3 text-sm">
        {run.state === 'DELIVERED' ? (
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
        ) : run.state === 'FAILED' ? (
          <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
        ) : (
          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-fg-subtle" />
        )}
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-medium text-fg">
            {STATE_COPY[run.state] ?? run.state}
            <Badge tone={runStateTone(run.state)}>{run.state}</Badge>
            {inFlight && <Loader2 className="h-3.5 w-3.5 animate-spin text-fg-subtle" />}
          </p>
          {run.agent && <p className="mt-0.5 text-xs text-fg-subtle">{run.agent.name}</p>}
          {run.reason && <p className="mt-0.5 whitespace-pre-wrap text-fg-muted">{run.reason}</p>}
        </div>
        {inFlight && (
          <Button variant="secondary" size="sm" className="ml-auto shrink-0" disabled={canceling} onClick={cancel}>
            {canceling ? 'Canceling…' : 'Cancel'}
          </Button>
        )}
        {canRetry && (
          <Button variant="primary" size="sm" className="ml-auto shrink-0" disabled={retrying} onClick={retry}>
            {retrying ? 'Starting…' : 'Run again'}
          </Button>
        )}
      </div>

      <TranscriptFeed events={run.events} inFlight={inFlight} />
    </div>
  );
}
